import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Lock, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney, friendlyError, getCheckoutConfig, startContentCheckout } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Devuelve el texto legal a mostrar bajo el CTA del paywall según el
 * provider ONE_TIME activo. No expone `mock_test` al usuario final.
 */
function providerNotice(provider) {
  if (provider === 'redsys') return 'Pago seguro gestionado por Redsys.';
  if (provider === 'mock_test') return 'Modo de prueba de pagos activo.';
  return null;
}

/**
 * Paywall reutilizable para Contenidos con `access_type = pay_per_content`.
 *
 * Diseño deliberadamente sencillo (Entrega 2):
 *   - Solo un CTA: "Comprar este contenido — X,XX €".
 *   - SIN ofertas Premium ni mensajes sobre suscripciones.
 *   - Tras checkout confirmado, se reanuda la reproducción inmediata
 *     gracias a `next` (la página de éxito redirige al watch original).
 *
 * Props:
 *   content: { id, title, price_cents, currency }
 *   eventSlug: string  (para el redirect post-pago)
 *   requiresLogin: boolean
 */
export function ContentPaywall({ content, eventSlug, requiresLogin = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [provider, setProvider] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getCheckoutConfig();
        if (alive) setProvider(cfg?.default_one_time_provider || null);
      } catch { /* silencioso: si falla, ocultamos el aviso */ }
    })();
    return () => { alive = false; };
  }, []);

  const price = formatMoney(content?.price_cents, content?.currency || 'EUR');
  // Tras el pago volvemos exactamente al contenido comprado (?v=<id>) para
  // que la reproducción se reanude sin caer en el contenido "por defecto"
  // del partido, que puede seguir bloqueado.
  const watchPath = eventSlug && content?.id
    ? `/eventos/${eventSlug}/watch?v=${content.id}`
    : (eventSlug ? `/eventos/${eventSlug}/watch` : '/eventos');
  const nextEncoded = encodeURIComponent(watchPath);

  const handleBuy = async () => {
    if (!user || requiresLogin) {
      toast.info('Inicia sesión para comprar este contenido.');
      navigate(`/acceder?next=${nextEncoded}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await startContentCheckout(content.id);
      navigate(`/checkout/sesion/${data.id}?next=${nextEncoded}`);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="relative overflow-hidden rounded-3xl border border-[var(--ns-gold)]/40 bg-gradient-to-br from-[var(--ns-bg-2)] to-white p-6 sm:p-8"
      data-testid="content-paywall"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-11 w-11 rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold mb-1">
            Compra individual
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-[var(--ns-text)]">
            Compra este contenido para reproducirlo
          </h3>
          <p className="text-sm text-[var(--ns-text-muted)] mt-1.5 leading-relaxed">
            Pago único. Tras confirmar, podrás reproducirlo de inmediato en esta misma pantalla.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <Button
          type="button"
          onClick={handleBuy}
          disabled={busy}
          className="h-12 w-full sm:w-auto rounded-xl bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold flex items-center justify-center gap-2 px-6"
          data-testid="content-paywall-buy"
        >
          <Ticket className="h-4 w-4" />
          {busy ? 'Procesando…' : `Comprar este contenido — ${price}`}
        </Button>
      </div>

      <p
        className="text-xs text-[var(--ns-text-subtle)] mt-4"
        data-testid="content-paywall-provider-notice"
      >
        {providerNotice(provider)}
      </p>
    </Card>
  );
}

export default ContentPaywall;
